import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCan } from "@/lib/auth";

import { useSaveGoal } from "../api";
import type { GoalRow } from "./GoalsTab";

/**
 * Quick view / expanded view for one feature's goal, opened by a row click.
 *
 * Same shape as the other record drawers in the app (contracts, customer
 * tiers): URL-driven open/expanded state so a goal is linkable and the back
 * button works.
 */
export function GoalDrawer({
  row,
  open,
  expanded,
  onOpenChange,
  onExpandedChange,
  onClose,
}: {
  row: GoalRow | null;
  open: boolean;
  expanded: boolean;
  onOpenChange: (open: boolean) => void;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}) {
  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title={row?.label ?? ""}
      subtitle={
        row ? (
          <span className="flex items-center gap-2">
            <span>{row.navGroup ?? "—"}</span>
            <span aria-hidden>·</span>
            <code className="rounded bg-muted px-1 text-xs">{row.featureKey}</code>
          </span>
        ) : null
      }
    >
      {row ? <GoalForm row={row} /> : null}
    </EntityDrawer>
  );
}

function GoalForm({ row }: { row: GoalRow }) {
  const canWrite = useCan("posthog.write");
  const save = useSaveGoal();
  const goal = row.goal;

  const [purpose, setPurpose] = useState(goal?.purpose ?? "");
  const [cadence, setCadence] = useState(goal?.cadence ?? "weekly");
  const [targetUsers, setTargetUsers] = useState(
    goal?.target_active_users?.toString() ?? "",
  );
  const [targetEvents, setTargetEvents] = useState(goal?.target_events?.toString() ?? "");
  const [notes, setNotes] = useState(goal?.notes ?? "");

  // The drawer stays mounted while the selected row changes underneath it, so
  // the form has to re-seed when a different feature is opened.
  useEffect(() => {
    setPurpose(goal?.purpose ?? "");
    setCadence(goal?.cadence ?? "weekly");
    setTargetUsers(goal?.target_active_users?.toString() ?? "");
    setTargetEvents(goal?.target_events?.toString() ?? "");
    setNotes(goal?.notes ?? "");
  }, [row.featureKey, goal]);

  const onSave = () =>
    save.mutate(
      {
        featureKey: row.featureKey,
        label: row.label,
        nav_group: row.navGroup,
        purpose: purpose.trim() || null,
        cadence,
        target_active_users: targetUsers ? Number(targetUsers) : null,
        target_events: targetEvents ? Number(targetEvents) : null,
        notes: notes.trim() || null,
      } as never,
      {
        onSuccess: () => toast.success(`Goal saved for ${row.label}`),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "Couldn't save the goal"),
      },
    );

  return (
    // EntityDrawer's content slot is an unpadded scroll container — each
    // consumer pads its own sections. p-4 matches the other record drawers
    // (contracts, cro_forecast); without it the form sits flush against the
    // drawer edges.
    <div className="flex flex-col gap-4 p-4">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Stat label="Purpose">
          {goal?.purpose ? (
            <StatusBadge value="complete" label="Set" />
          ) : (
            <StatusBadge value="pending" label="Not set" />
          )}
        </Stat>
        <Stat label="Opens">{row.events.toLocaleString()}</Stat>
        <Stat label="People">{row.users}</Stat>
        <Stat label="Cadence">{goal?.cadence ?? "—"}</Stat>
      </dl>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="goal-purpose">What is this feature for?</Label>
        <Textarea
          id="goal-purpose"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          disabled={!canWrite}
          rows={expandedRows(purpose)}
          placeholder="e.g. So CSMs can catch at-risk renewals 90 days out, before the customer starts shopping."
        />
        <p className="text-xs text-muted-foreground">
          Write it as an outcome, not a description. This is the sentence the weekly
          review argues against.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="goal-cadence">Expected cadence</Label>
          <Select value={cadence} onValueChange={setCadence} disabled={!canWrite}>
            <SelectTrigger id="goal-cadence">
              <SelectValue placeholder="Cadence" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="goal-users">Target people</Label>
          <Input
            id="goal-users"
            type="number"
            min={0}
            value={targetUsers}
            onChange={(e) => setTargetUsers(e.target.value)}
            disabled={!canWrite}
            placeholder="e.g. 4"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="goal-events">Target opens</Label>
          <Input
            id="goal-events"
            type="number"
            min={0}
            value={targetEvents}
            onChange={(e) => setTargetEvents(e.target.value)}
            disabled={!canWrite}
            placeholder="per period"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="goal-notes">Notes</Label>
        <Textarea
          id="goal-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={!canWrite}
          rows={2}
          placeholder="Anything the reviewer should know — seasonality, who it was built for, why low usage is expected."
        />
      </div>

      {goal?.user_goals?.length ? (
        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold">Per-person expectations</div>
          <ul className="flex flex-col gap-1 text-sm">
            {goal.user_goals.map((ug) => (
              <li
                key={ug.user_id}
                className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-1.5"
              >
                <span>{ug.name ?? ug.email ?? ug.user_id}</span>
                <span className="text-muted-foreground">
                  {ug.target_events != null ? `${ug.target_events} / ${ug.cadence}` : ug.cadence}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {canWrite ? (
        <div className="flex justify-end">
          <Button onClick={onSave} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save goal"}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          You need <code className="rounded bg-muted px-1">posthog.write</code> to edit
          goals.
        </p>
      )}
    </div>
  );
}

/** Grow the purpose box a little once there's real text in it. */
function expandedRows(text: string): number {
  return text.length > 160 ? 5 : 3;
}

/**
 * Read-only label/value pair. Mirrors the local helper in the contracts
 * RecordDrawer — the shared `Field` component is a form-field wrapper (it
 * requires an id and renders error/helper text), which is the wrong thing for
 * a summary row.
 */
function Stat({ label, children }: { label: string; children?: ReactNode }) {
  const empty = children == null || children === "" || children === "—";
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="truncate text-sm text-foreground">{empty ? "—" : children}</dd>
    </div>
  );
}
